import { Suspense, useEffect } from 'react';

import {
    TFormalOffer,
    TFormalOffers
} from '@helpers/FormalOffers/formalOffers.types';
import { getFormalOffersMaxInterestOnlyPeriod } from '@helpers/FormalOffers/getFormalOffersMaxInterestOnlyPeriod';
import { getCountry } from '@iwIntlProvider/CountryProvider';
import { Radio } from '@iwoca/orion';
import { FormattedMessage, useIntl } from 'react-intl';

import messages from './selectOfferType.messages';
import { useGetSelectOfferTypeData } from './useGetSelectOfferTypeData';
import { ErrorBoundary } from '../../../../../errorBoundary/ErrorBoundary';
import FallbackErrorModal from '../../../../modals/fallbackErrorModal/FallbackErrorModal';
import MainContentPending from '../../../../pageStructure/mainContentPending';

export const SelectOfferType = ({
    setOfferChoices,
    drawdownAmount,
    selectedOffer,
    updateSelectedOffer
}: {
    setOfferChoices: (formalOffers: TFormalOffers) => void;
    updateSelectedOffer: (offer: TFormalOffer | null) => void;
    selectedOffer: TFormalOffer | null;
    drawdownAmount: number;
}) => {
    return (
        <ErrorBoundary fallback={() => <FallbackErrorModal />}>
            <Suspense
                fallback={
                    <div
                        className="flex-1 rounded-m bg-structure-100 p-l shadow-black-s"
                        data-testId="loan-overview-loading"
                    >
                        <MainContentPending />
                    </div>
                }
            >
                <SelectOfferTypeButtons
                    setOfferChoices={setOfferChoices}
                    drawdownAmount={drawdownAmount}
                    selectedOffer={selectedOffer}
                    updateSelectedOffer={updateSelectedOffer}
                />
            </Suspense>
        </ErrorBoundary>
    );
};

const SelectOfferTypeButtons = ({
    setOfferChoices,
    drawdownAmount,
    selectedOffer,
    updateSelectedOffer
}: {
    setOfferChoices: (formalOffers: TFormalOffers) => void;
    updateSelectedOffer: (offer: TFormalOffer | null) => void;
    selectedOffer: TFormalOffer | null;
    drawdownAmount: number;
}) => {
    const {
        longerDurationOffers,
        interestOnlyOffers,
        rblOffers,
        flexibleOffers,
        maxFlexibleLoanYear,
        shouldUseInterestOnlyPlusCopy
    } = useGetSelectOfferTypeData();
    const intl = useIntl();

    const isDrawdownAmountInRange = (offers: TFormalOffers) => {
        return offers.every((offer) => drawdownAmount > offer.available_cash!);
    };

    const handleOptionSelected = (offers: TFormalOffers) => {
        setOfferChoices(offers);
        updateSelectedOffer(null);
    };

    const flexibleOfferSelected = Boolean(
        flexibleOffers.find((offer) => offer === selectedOffer)
    );

    const interestOnlyOfferSelected = Boolean(
        interestOnlyOffers.find((offer) => offer === selectedOffer)
    );

    // This effect ensures the correct offers are displayed when a customer's offer configuration has been persisted and their choice was a flexi loan,
    // where they had multiple duration choices. Without this effect the duration buttons would be hidden
    // See - https://git.iwoca.co.uk/iwoca/pandora/-/merge_requests/5649#note_1658851
    useEffect(() => {
        if (getCountry() === 'DE' && flexibleOfferSelected) {
            setOfferChoices(flexibleOffers);
        }
    }, [flexibleOfferSelected, setOfferChoices, flexibleOffers]);

    if (interestOnlyOffers.length > 0) {
        return (
            <>
                <p className="text-m font-bold">
                    <FormattedMessage
                        id="_.offer_type_select.interest_only_offer_type_heading"
                        defaultMessage="I want my offer terms to be"
                    />
                </p>

                <div className="flex flex-col gap-m">
                    <Radio
                        frame
                        id="flexible-loans-offers"
                        label={
                            (
                                <p className="w-10/12 font-reg">
                                    <FormattedMessage
                                        tagName="span"
                                        {...messages.flexibleLoansTitle_withIOoption}
                                        values={{
                                            strong: (chunks) => (
                                                <strong>{chunks}</strong>
                                            )
                                        }}
                                    />
                                </p>
                            ) as unknown as string
                        }
                        name="offer-selection-buttons"
                        onChange={() => handleOptionSelected(flexibleOffers)}
                        disabled={isDrawdownAmountInRange(flexibleOffers)}
                        checked={flexibleOfferSelected}
                    />

                    <Radio
                        frame
                        id="interest-only-loans-offers"
                        label={
                            (
                                <p className="w-10/12 font-reg">
                                    {shouldUseInterestOnlyPlusCopy ? (
                                        <FormattedMessage
                                            tagName="span"
                                            id="_.offer_type_select.interest_only_plus_loans_title"
                                            defaultMessage="<strong>Flexible credit line:</strong> liquidity as needed"
                                            values={{
                                                strong: (chunks) => (
                                                    <strong>{chunks}</strong>
                                                )
                                            }}
                                        />
                                    ) : (
                                        <FormattedMessage
                                            tagName="span"
                                            id="_.offer_type_select.interest_only_loans_title"
                                            defaultMessage="<strong>Interest Only:</strong> pay only interest for the first {maxInterestOnlyPeriod} months"
                                            values={{
                                                strong: (chunks) => (
                                                    <strong>{chunks}</strong>
                                                ),
                                                maxInterestOnlyPeriod:
                                                    getFormalOffersMaxInterestOnlyPeriod(
                                                        interestOnlyOffers
                                                    )
                                            }}
                                        />
                                    )}
                                </p>
                            ) as unknown as string
                        }
                        name="offer-selection-buttons"
                        onChange={() =>
                            handleOptionSelected(interestOnlyOffers)
                        }
                        disabled={isDrawdownAmountInRange(interestOnlyOffers)}
                        checked={interestOnlyOfferSelected}
                    />
                </div>
            </>
        );
    }

    if (rblOffers.length > 0) {
        const rblOfferSelected = selectedOffer === rblOffers[0];

        return (
            <>
                <p className="text-m font-bold">
                    {intl.formatMessage(messages.longerLoansOfferTypeHeading)}
                </p>

                <div className="flex flex-col gap-m">
                    <Radio
                        frame
                        id="flexible-loans-offers"
                        label={
                            (
                                <p className="w-10/12 font-reg">
                                    <FormattedMessage
                                        tagName="span"
                                        {...messages.flexibleLoansTitle}
                                        values={{
                                            strong: (chunks) => (
                                                <strong>{chunks}</strong>
                                            ),
                                            maxYear: maxFlexibleLoanYear
                                        }}
                                    />
                                </p>
                            ) as unknown as string
                        }
                        name="offer-selection-buttons"
                        onChange={() => handleOptionSelected(flexibleOffers)}
                        disabled={isDrawdownAmountInRange(flexibleOffers)}
                        checked={flexibleOfferSelected}
                    />

                    <Radio
                        frame
                        id="longer-loans-offers"
                        label={
                            (
                                <p className="w-10/12 font-reg">
                                    <FormattedMessage
                                        tagName="span"
                                        {...messages.rblTitle}
                                        values={{
                                            strong: (chunks) => (
                                                <strong>{chunks}</strong>
                                            )
                                        }}
                                    />
                                </p>
                            ) as unknown as string
                        }
                        name="offer-selection-buttons"
                        onChange={() => {
                            setOfferChoices(rblOffers);
                            updateSelectedOffer(rblOffers[0]);
                        }}
                        disabled={isDrawdownAmountInRange(rblOffers)}
                        checked={rblOfferSelected}
                    />
                </div>
            </>
        );
    }

    return (
        <>
            <p className="text-m font-bold">
                {intl.formatMessage(messages.longerLoansOfferTypeHeading)}
            </p>

            <div className="flex flex-col gap-m">
                <Radio
                    frame
                    id="flexible-loans-offers"
                    label={
                        (
                            <p className="w-10/12 font-reg">
                                <FormattedMessage
                                    tagName="span"
                                    {...messages.flexibleLoansTitle}
                                    values={{
                                        strong: (chunks) => (
                                            <strong>{chunks}</strong>
                                        ),
                                        maxYear: maxFlexibleLoanYear
                                    }}
                                />
                            </p>
                        ) as unknown as string
                    }
                    name="offer-selection-buttons"
                    onChange={() => handleOptionSelected(flexibleOffers)}
                    disabled={isDrawdownAmountInRange(flexibleOffers)}
                />

                <Radio
                    frame
                    id="longer-loans-offers"
                    label={
                        (
                            <p className="w-10/12 font-reg">
                                <FormattedMessage
                                    tagName="span"
                                    {...messages.longerLoansTitle}
                                    values={{
                                        strong: (chunks) => (
                                            <strong>{chunks}</strong>
                                        )
                                    }}
                                />
                            </p>
                        ) as unknown as string
                    }
                    name="offer-selection-buttons"
                    onChange={() => handleOptionSelected(longerDurationOffers)}
                    disabled={isDrawdownAmountInRange(longerDurationOffers)}
                />
            </div>
        </>
    );
};
